import React, { useState } from "react";
import {
  FolderOpen,
  Plus,
  Search,
  FileText,
  FileCode,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  Cpu,
  Trash2,
} from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { OrgDocument } from "../types";
import { getErrorMessage } from "../types";
import { OrgDocumentUploadModal, Modal, ConfirmActionModal, type ConfirmAction } from "../components/modals";

export interface DocumentsPageProps {
  slug: string;
  documents: OrgDocument[];
  loadDocuments: (force?: boolean) => Promise<void>;
}

export const DocumentsPage: React.FC<DocumentsPageProps> = ({
  slug,
  documents,
  loadDocuments,
}) => {
  const [showDocModal, setShowDocModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<OrgDocument | null>(null);
  const [vectorizingDocId, setVectorizingDocId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const resolveDocUrl = (url: string) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const rawApiBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "";
    const cleanedBase = rawApiBase.replace(/\/api\/v1\/?$/, "");
    return `${cleanedBase}${url}`;
  };

  // RAG Search States
  const [ragQuery, setRagQuery] = useState("");
  const [searchingRAG, setSearchingRAG] = useState(false);
  const [ragResults, setRagResults] = useState<any[]>([]);

  const handleSearchRAG = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ragQuery.trim()) return;
    try {
      setSearchingRAG(true);
      const { data } = await api.searchOrganizationKnowledgeBaseApiV1B2bSlugKnowledgeBaseSearchPost({
        path: { slug },
        body: { query: ragQuery.trim(), top_k: 4 },
      });
      if (data) {
        setRagResults((data as any).results || []);
      }
    } catch (err) {
      console.error("Failed to search knowledge base:", err);
    } finally {
      setSearchingRAG(false);
    }
  };

  const handleVectorizeDoc = async (docId: number) => {
    try {
      setVectorizingDocId(docId);
      const { data, error } = await api.triggerDocumentVectorizationApiV1B2bSlugDocumentsDocIdVectorizePost({
        path: { slug, doc_id: docId },
      });
      if (data) {
        await loadDocuments(true);
        alert((data as any).message || "Document vectorized successfully!");
      } else {
        alert(getErrorMessage(error, "Vectorization failed."));
      }
    } catch (err) {
      console.error("Failed to vectorize document:", err);
    } finally {
      setVectorizingDocId(null);
    }
  };

  const handleDeleteDocument = (docId: number) => {
    if (!slug) return;
    const document = documents.find((item) => item.id === docId);
    setConfirmAction({
      title: "Delete organization document?",
      description: `Delete ${document?.title || "this document"}? It will no longer be available in the organization knowledge base.`,
      confirmLabel: "Delete document",
      onConfirm: async () => {
        try {
          const { error } = await api.deleteOrgDocumentApiV1B2bSlugDocumentsDocIdDelete({
            path: { slug, doc_id: docId },
          });
          if (!error) {
            await loadDocuments(true);
          } else {
            alert(getErrorMessage(error, "Failed to delete document."));
          }
        } catch (err) {
          console.error("Failed to delete document:", err);
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <ConfirmActionModal action={confirmAction} onClose={() => setConfirmAction(null)} />
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-xs">
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-indigo-600" />
            Organization SOPs &amp; Knowledge Documents
          </h3>
          <p className="text-xs text-slate-500 max-w-xl">
            Upload internal standard operating procedures (SOPs), company policies, and training manuals.
            These documents are vectorized using pgvector embeddings for grounding AI lessons and training modules.
          </p>
        </div>

        <button
          onClick={() => setShowDocModal(true)}
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-2 shadow-xs transition shrink-0 active:scale-95"
        >
          <Plus className="w-4 h-4" />
          Upload Document
        </button>
      </div>

      {/* RAG Semantic Search Console */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-indigo-600" />
          Semantic Knowledge Search (RAG Test Console)
        </h4>
        <form onSubmit={handleSearchRAG} className="flex gap-2">
          <input
            type="text"
            placeholder="Ask a question or search internal policies / SOPs..."
            value={ragQuery}
            onChange={(e) => setRagQuery(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-500 shadow-xs"
          />
          <button
            type="submit"
            disabled={searchingRAG}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shrink-0 shadow-xs disabled:opacity-50"
          >
            {searchingRAG ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Searching...
              </>
            ) : (
              "Vector Search"
            )}
          </button>
        </form>

        {ragResults.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            {ragResults.map((r: any, i: number) => (
              <div key={i} className="p-3.5 bg-white border border-slate-200 rounded-xl text-xs shadow-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold text-indigo-700 truncate">{r.document_title}</span>
                  <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-mono rounded">
                    {Math.round((r.similarity_score ?? 0) * 100)}% match
                  </span>
                </div>
                <p className="text-slate-600 leading-relaxed text-[11px] whitespace-pre-wrap">{r.chunk_text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {documents.length === 0 ? (
          <div className="col-span-full py-12 text-center text-slate-500 bg-white border border-slate-200 rounded-3xl shadow-xs">
            <FileText className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p className="text-sm font-bold text-slate-700">No company documents uploaded yet.</p>
            <p className="text-xs text-slate-500 mt-1">
              Click &quot;Upload Document&quot; above to add your first SOP or policy.
            </p>
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="p-5 bg-white border border-slate-200 rounded-2xl flex items-start justify-between gap-4 shadow-xs hover:border-slate-300 transition"
            >
              <div className="flex items-start gap-3.5 flex-1 min-w-0">
                <div className="p-3 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-xl shrink-0 mt-0.5">
                  <FileCode className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold uppercase rounded-md">
                      {doc.document_type}
                    </span>
                    {doc.status === "ready" && (
                      <span className="px-2 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold rounded-md flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Ready · {doc.chunks_count || 0} chunks
                      </span>
                    )}
                    {doc.status === "processing" && (
                      <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold rounded-md animate-pulse flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Vectorizing...
                      </span>
                    )}
                    {doc.status === "failed" && (
                      <span className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-semibold rounded-md flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Vectorization Failed
                      </span>
                    )}
                    {doc.file_size_bytes && (
                      <span className="text-[10px] text-slate-400 font-mono">
                        {doc.file_size_bytes > 1048576
                          ? `${(doc.file_size_bytes / 1048576).toFixed(1)} MB`
                          : `${(doc.file_size_bytes / 1024).toFixed(1)} KB`}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 truncate">{doc.title}</h4>
                  {doc.description && (
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed line-clamp-2">
                      {doc.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      type="button"
                      onClick={() => setPreviewDoc(doc)}
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-semibold underline"
                    >
                      View Document
                      <ExternalLink className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleVectorizeDoc(doc.id)}
                      disabled={vectorizingDocId === doc.id}
                      className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 text-[11px] font-semibold rounded-lg flex items-center gap-1 transition disabled:opacity-50"
                    >
                      <Cpu className="w-3 h-3" />
                      {vectorizingDocId === doc.id
                        ? "Vectorizing..."
                        : doc.status === "ready"
                        ? "Re-Vectorize"
                        : "Vectorize for AI"}
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleDeleteDocument(doc.id)}
                className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg transition shrink-0"
                title="Delete document"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Document Preview Modal */}
      <Modal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.title || "Document Details"}
        subtitle={`Type: ${previewDoc?.document_type?.toUpperCase() || "DOCUMENT"} • Added ${previewDoc?.created_at ? new Date(previewDoc.created_at).toLocaleDateString() : ""}`}
        icon={<FileText className="w-5 h-5 text-indigo-600" />}
        maxWidth="max-w-xl"
      >
        {previewDoc && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-2xl text-xs">
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">File Size</span>
                <span className="font-semibold text-slate-800">
                  {previewDoc.file_size_bytes
                    ? previewDoc.file_size_bytes > 1048576
                      ? `${(previewDoc.file_size_bytes / 1048576).toFixed(1)} MB`
                      : `${(previewDoc.file_size_bytes / 1024).toFixed(1)} KB`
                    : "—"}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">AI Status</span>
                <span className="font-semibold text-slate-800 capitalize">
                  {previewDoc.status || "Ready"} ({previewDoc.chunks_count || 0} chunks)
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold tracking-wider">Type</span>
                <span className="font-semibold text-slate-800 uppercase">{previewDoc.document_type}</span>
              </div>
            </div>

            {previewDoc.description && (
              <div>
                <h5 className="text-xs font-bold text-slate-700 mb-1">Description</h5>
                <p className="text-xs text-slate-600 leading-relaxed bg-white p-3 border border-slate-200 rounded-xl">
                  {previewDoc.description}
                </p>
              </div>
            )}

            <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-xs text-indigo-950 overflow-hidden">
                <p className="font-bold">Original File</p>
                <p className="text-[11px] text-indigo-700/80 truncate max-w-xs sm:max-w-sm">
                  {previewDoc.file_url}
                </p>
              </div>
              <a
                href={resolveDocUrl(previewDoc.file_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-xl inline-flex items-center gap-1.5 transition shrink-0 shadow-xs"
              >
                <span>Open / Download</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl hover:bg-slate-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Upload Modal */}
      <OrgDocumentUploadModal
        isOpen={showDocModal}
        onClose={() => setShowDocModal(false)}
        slug={slug}
        onSuccess={async () => {
          await loadDocuments(true);
        }}
      />
    </div>
  );
};
